import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { Button } from './ui/Button';
import { Alert } from './ui/Alert';
import { apiFetch, parseJsonResponse, isNetworkError, isNetworkErrorResponse } from '../utils/api';

interface Font {
  id: number;
  name: string;
  display_name: string;
  font_family: string;
  font_file_path: string | null;
  font_preview_image: string | null;
  is_google_font: number;
  google_font_url: string | null;
  font_weight: string;
  font_style: string;
  category: string;
  tags: string[] | string | null;
  is_active: number;
  is_premium: number;
}

interface FontFormProps {
  font?: Font | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export const FontForm: React.FC<FontFormProps> = ({ font, onSuccess, onCancel }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fontFile, setFontFile] = useState<File | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [uploadingFont, setUploadingFont] = useState(false);
  const [uploadingPreview, setUploadingPreview] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    font_family: '',
    font_file_path: '',
    font_preview_image: '',
    is_google_font: false,
    google_font_url: '',
    font_weight: '400',
    font_style: 'normal',
    category: 'sans-serif',
    tags: '',
    is_active: true,
    is_premium: false
  });

  useEffect(() => {
    if (font) {
      // Convertir tags de array a string separada por comas
      let tagsStr = '';
      if (font.tags) {
        if (Array.isArray(font.tags)) {
          tagsStr = font.tags.join(', ');
        } else if (typeof font.tags === 'string') {
          try {
            const parsed = JSON.parse(font.tags);
            tagsStr = Array.isArray(parsed) ? parsed.join(', ') : font.tags;
          } catch {
            tagsStr = font.tags;
          }
        }
      }

      setFormData({
        name: font.name || '',
        display_name: font.display_name || '',
        font_family: font.font_family || '',
        font_file_path: font.font_file_path || '',
        font_preview_image: font.font_preview_image || '',
        is_google_font: font.is_google_font === 1,
        google_font_url: font.google_font_url || '',
        font_weight: font.font_weight || '400',
        font_style: font.font_style || 'normal',
        category: font.category || 'sans-serif',
        tags: tagsStr,
        is_active: font.is_active === 1,
        is_premium: font.is_premium === 1
      });
    }
  }, [font]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    });
  };

  const handleFontFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar extensión
      const allowedExtensions = ['woff', 'woff2', 'ttf', 'otf', 'eot'];
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!extension || !allowedExtensions.includes(extension)) {
        setError('Tipo de archivo no permitido. Extensiones permitidas: ' + allowedExtensions.join(', '));
        e.target.value = '';
        return;
      }
      // Validar tamaño (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('El archivo es demasiado grande. Tamaño máximo: 10MB');
        e.target.value = '';
        return;
      }
      setFontFile(file);
      setError(null);
    }
  };

  const handlePreviewFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tipo de imagen
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Tipo de imagen no permitido. Tipos permitidos: JPG, PNG, GIF, WEBP');
        e.target.value = '';
        return;
      }
      // Validar tamaño (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('La imagen es demasiado grande. Tamaño máximo: 5MB');
        e.target.value = '';
        return;
      }
      setPreviewFile(file);
      setError(null);
    }
  };

  const uploadFontFile = async (): Promise<string | null> => {
    if (!fontFile || !token) return null;

    setUploadingFont(true);
    try {
      const formData = new FormData();
      formData.append('font_file', fontFile);

      const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://hiomenu.local/api';
      const res = await fetch(`${API_BASE_URL}.php?route=fonts/upload-font`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || json.message || 'Error al subir archivo de fuente');
      }

      const json = await res.json();
      return json.data?.file_path || null;
    } catch (err: any) {
      throw new Error(err.message || 'Error al subir archivo de fuente');
    } finally {
      setUploadingFont(false);
    }
  };

  const uploadPreviewImage = async (): Promise<string | null> => {
    if (!previewFile || !token) return null;

    setUploadingPreview(true);
    try {
      const formData = new FormData();
      formData.append('preview_image', previewFile);

      const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://hiomenu.local/api';
      const res = await fetch(`${API_BASE_URL}.php?route=fonts/upload-preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || json.message || 'Error al subir imagen de preview');
      }

      const json = await res.json();
      return json.data?.image_path || null;
    } catch (err: any) {
      throw new Error(err.message || 'Error al subir imagen de preview');
    } finally {
      setUploadingPreview(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setError('No hay token de autenticación');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      // Subir archivos primero si existen
      let fontFilePath: string | null = formData.font_file_path || null;
      let previewImagePath: string | null = formData.font_preview_image || null;

      // Si hay un nuevo archivo de fuente, subirlo
      if (fontFile) {
        const uploadedPath = await uploadFontFile();
        if (!uploadedPath) {
          throw new Error('No se pudo subir el archivo de fuente');
        }
        fontFilePath = uploadedPath;
      }

      // Si hay una nueva imagen de preview, subirla
      if (previewFile) {
        const uploadedPath = await uploadPreviewImage();
        if (!uploadedPath) {
          throw new Error('No se pudo subir la imagen de preview');
        }
        previewImagePath = uploadedPath;
      }

      const isEdit = !!font;
      const route = isEdit ? 'fonts/update' : 'fonts/create';

      // Procesar tags: convertir string separado por comas a array
      const tagsArray = formData.tags
        ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
        : [];

      const payload: any = {
        name: formData.name,
        display_name: formData.display_name,
        font_family: formData.font_family,
        font_file_path: fontFilePath || null,
        font_preview_image: previewImagePath || null,
        is_google_font: formData.is_google_font ? 1 : 0,
        google_font_url: formData.is_google_font ? (formData.google_font_url || null) : null,
        font_weight: formData.font_weight,
        font_style: formData.font_style,
        category: formData.category,
        tags: tagsArray,
        is_active: formData.is_active ? 1 : 0,
        is_premium: formData.is_premium ? 1 : 0
      };

      if (isEdit) {
        payload.id = font.id;
      }

      const res = await apiFetch(route, {
        method: 'POST',
        body: JSON.stringify(payload)
      }, token);

      // Si es error de red, mostrar mensaje y mantener el formulario abierto
      if (isNetworkErrorResponse(res)) {
        setError('Error de conexión. Verifica tu conexión a internet e intenta de nuevo.');
        return;
      }

      const json = await parseJsonResponse(res);

      if (!res.ok || json.error) {
        throw new Error(json.error || json.message || json.data?.error || 'Error al guardar fuente');
      }

      onSuccess();
    } catch (err: any) {
      if (isNetworkError(err)) {
        setError('Error de conexión. Verifica tu conexión a internet e intenta de nuevo.');
      } else {
        setError(err.message || 'Error inesperado');
      }
    } finally {
      setLoading(false);
    }
  };

  const categoryOptions = [
    { value: 'sans-serif', label: 'Sans Serif' },
    { value: 'serif', label: 'Serif' },
    { value: 'display', label: 'Display' },
    { value: 'handwriting', label: 'Handwriting' },
    { value: 'monospace', label: 'Monospace' }
  ];

  const fontWeightOptions = [
    { value: '400', label: '400 (Normal)' },
    { value: '500', label: '500 (Medium)' },
    { value: '600', label: '600 (Semibold)' },
    { value: '700', label: '700 (Bold)' }
  ];

  const fontStyleOptions = [
    { value: 'normal', label: 'Normal' },
    { value: 'italic', label: 'Italic' }
  ];

  return (
    <div className="w-full">
      {error && (
        <div className="mb-6">
          <Alert variant="error">{error}</Alert>
        </div>
      )}

      <form onSubmit={handleSubmit} className="w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            type="text"
            label="Nombre Interno"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Ej: roboto-regular"
            helperText="Nombre único para identificar la fuente (sin espacios, usar guiones)"
          />

          <Input
            type="text"
            label="Nombre para Mostrar"
            name="display_name"
            value={formData.display_name}
            onChange={handleChange}
            required
            placeholder="Ej: Roboto Regular"
          />

          <div className="md:col-span-2">
            <Input
              type="text"
              label="Familia de Fuente CSS"
              name="font_family"
              value={formData.font_family}
              onChange={handleChange}
              required
              placeholder="Ej: 'Roboto', sans-serif"
              helperText="Familia de fuente como se usará en CSS"
            />
          </div>

          <div className="md:col-span-2">
            <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
              <input
                type="checkbox"
                name="is_google_font"
                checked={formData.is_google_font}
                onChange={handleChange}
                className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
              />
              <div>
                <span className="text-sm font-semibold text-gray-900">Fuente de Google Fonts</span>
                <p className="text-xs text-gray-500 mt-0.5">Marca si la fuente proviene de Google Fonts</p>
              </div>
            </label>
          </div>

          {formData.is_google_font && (
            <div className="md:col-span-2">
              <Input
                type="url"
                label="URL de Google Fonts"
                name="google_font_url"
                value={formData.google_font_url}
                onChange={handleChange}
                placeholder="https://fonts.googleapis.com/css2?family=Roboto"
                helperText="URL completa de la fuente en Google Fonts"
              />
            </div>
          )}

          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Archivo de Fuente
              {!formData.is_google_font && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="space-y-2">
              <input
                type="file"
                accept=".woff,.woff2,.ttf,.otf,.eot"
                onChange={handleFontFileChange}
                className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                disabled={formData.is_google_font || loading || uploadingFont}
              />
              {fontFile && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{fontFile.name}</span>
                  <span className="text-gray-400">({(fontFile.size / 1024).toFixed(2)} KB)</span>
                </div>
              )}
              {formData.font_file_path && !fontFile && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Archivo actual: {formData.font_file_path}</span>
                </div>
              )}
              {uploadingFont && (
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span>Subiendo archivo...</span>
                </div>
              )}
            </div>
            <p className="mt-1.5 text-sm text-gray-500">
              {formData.is_google_font 
                ? 'No es necesario subir archivo si es Google Fonts' 
                : 'Sube un archivo .woff2, .ttf, .otf, .eot (máx. 10MB)'}
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Imagen de Preview
            </label>
            <div className="space-y-2">
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={handlePreviewFileChange}
                className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                disabled={loading || uploadingPreview}
              />
              <p className="mt-1 text-xs text-gray-500">
                Formatos permitidos: JPG, PNG, GIF, WEBP. Tamaño máximo: 5MB
              </p>
              {previewFile && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{previewFile.name}</span>
                  <span className="text-gray-400">({(previewFile.size / 1024).toFixed(2)} KB)</span>
                </div>
              )}
              {previewFile && (
                <div className="mt-2">
                  <img 
                    src={URL.createObjectURL(previewFile)} 
                    alt="Preview" 
                    className="max-w-full h-32 object-contain border border-gray-200 rounded-lg"
                  />
                </div>
              )}
              {formData.font_preview_image && !previewFile && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Imagen actual: {formData.font_preview_image}</span>
                  {formData.font_preview_image.startsWith('/uploads/') && (
                    <img 
                      src={formData.font_preview_image} 
                      alt="Preview actual" 
                      className="max-w-full h-32 object-contain border border-gray-200 rounded-lg ml-2"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  )}
                </div>
              )}
              {uploadingPreview && (
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span>Subiendo imagen...</span>
                </div>
              )}
            </div>
            <p className="mt-1.5 text-sm text-gray-500">
              Sube una imagen de preview de la fuente (JPG, PNG, GIF, WEBP - máx. 5MB)
            </p>
          </div>

          <Select
            label="Peso de la Fuente"
            name="font_weight"
            value={formData.font_weight}
            onChange={handleChange}
            options={fontWeightOptions}
            required
          />

          <Select
            label="Estilo de la Fuente"
            name="font_style"
            value={formData.font_style}
            onChange={handleChange}
            options={fontStyleOptions}
            required
          />

          <Select
            label="Categoría"
            name="category"
            value={formData.category}
            onChange={handleChange}
            options={categoryOptions}
            required
          />

          <Input
            type="text"
            label="Tags (separados por comas)"
            name="tags"
            value={formData.tags}
            onChange={handleChange}
            placeholder="elegant, modern, classic"
            helperText="Tags para filtrado, separados por comas"
          />

          <div className="md:col-span-2">
            <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
              />
              <div>
                <span className="text-sm font-semibold text-gray-900">Fuente Activa</span>
                <p className="text-xs text-gray-500 mt-0.5">Las fuentes inactivas no estarán disponibles para los clientes</p>
              </div>
            </label>
          </div>

          <div className="md:col-span-2">
            <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
              <input
                type="checkbox"
                name="is_premium"
                checked={formData.is_premium}
                onChange={handleChange}
                className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
              />
              <div>
                <span className="text-sm font-semibold text-gray-900">Fuente Premium</span>
                <p className="text-xs text-gray-500 mt-0.5">Marca si esta fuente requiere licencia premium</p>
              </div>
            </label>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6 mt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            isLoading={loading || uploadingFont || uploadingPreview}
            disabled={loading || uploadingFont || uploadingPreview}
          >
            {uploadingFont || uploadingPreview 
              ? 'Subiendo archivos...' 
              : font ? 'Actualizar Fuente' : 'Crear Fuente'}
          </Button>
        </div>
      </form>
    </div>
  );
};

